import { useEffect, useMemo } from "react";

type JsonLd = Record<string, unknown>;

const ATTR = "data-page-schema";

function inject(schemas: JsonLd[]) {
  document.querySelectorAll(`script[${ATTR}]`).forEach((el) => el.remove());
  schemas.forEach((schema, i) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(ATTR, String(i));
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
  });
}

export function usePageSchema(schemas: JsonLd | JsonLd[] | null) {
  const list = useMemo(
    () => (schemas ? (Array.isArray(schemas) ? schemas : [schemas]) : []),
    [schemas]
  );
  useEffect(() => {
    if (!list.length) return;
    inject(list);
    return () => {
      document.querySelectorAll(`script[${ATTR}]`).forEach((el) => el.remove());
    };
  }, [list]);
}

type ServiceSchemaArgs = {
  name: string;
  description: string;
  url: string;
  price?: string;
  priceUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR" | "ONETIME";
  areaServed?: string;
  providerId: string;
};

export function serviceSchema(a: ServiceSchemaArgs): JsonLd {
  const offer: JsonLd = {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    description: a.description,
    ...(a.price && { price: a.price, priceCurrency: "USD" }),
    ...(a.price &&
      a.priceUnit && {
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: a.price,
          priceCurrency: "USD",
          unitText: a.priceUnit,
        },
      }),
  };
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${a.url}#service`,
    name: a.name,
    description: a.description,
    url: a.url,
    serviceType: a.name,
    provider: { "@id": a.providerId },
    ...(a.areaServed && { areaServed: { "@type": "State", name: a.areaServed } }),
    offers: offer,
  };
}

export function faqSchema(faqs: { q: string; a: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(
  items: { name: string; url: string }[]
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function localBusinessSchema(a: {
  name: string;
  description: string;
  url: string;
  areaServed: string[]; // city names
  telephone?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${a.url}#localbusiness`,
    name: a.name,
    description: a.description,
    url: a.url,
    telephone: a.telephone || "+1-954-296-4896",
    priceRange: "$$$-$$$$",
    address: {
      "@type": "PostalAddress",
      addressRegion: "FL",
      addressCountry: "US",
    },
    areaServed: a.areaServed.map((c) => ({ "@type": "City", name: c })),
  };
}

export function videoSchema(a: {
  name: string;
  description: string;
  url: string;
  thumbnailUrl?: string;
  uploadDate: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: a.name,
    description: a.description,
    thumbnailUrl: a.thumbnailUrl || "https://floridaaimarketingexperts.com/logo.png",
    uploadDate: a.uploadDate,
    contentUrl: a.url,
    publisher: {
      "@type": "Organization",
      name: "Florida AI Marketing Experts",
      logo: {
        "@type": "ImageObject",
        url: "https://floridaaimarketingexperts.com/logo.png",
      },
    },
  };
}

export function articleSchema(a: {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.headline,
    description: a.description,
    url: a.url,
    ...(a.image && { image: a.image }),
    datePublished: a.datePublished,
    dateModified: a.dateModified || a.datePublished,
    author: { "@type": "Organization", name: a.authorName || "Florida AI Marketing Experts" },
  };
}
